
import React from 'react';
import { KaiColor } from '../types';

interface Props {
  className?: string;
  size?: number;
  animated?: boolean;
  variant?: 'symbol' | 'face'; // Symbol = Crystalline Container, Face = Liquid Avatar Eye
  color?: KaiColor; // Added color support
}

/**
 * KaiLogo - The Visual Identity
 * 
 * DESIGN CONCEPT: "The Crystalline Container" (V4.5)
 * 
 * 1. The Container (Life OS): A 4-lobed frosted glass shell representing structure, 
 *    logic, and the 4 pillars (Passion, Mission, Profession, Vocation).
 * 
 * 2. The Core (Liquid Soul): A glowing singularity inside the container, 
 *    representing the user's purpose (Ikigai) powering the system.
 */
export const KaiLogo: React.FC<Props> = ({ className = "", size = 64, animated = true, variant = 'symbol', color = 'indigo' }) => {
  
  // Use React.useId to generate unique IDs for SVG defs to prevent conflicts when multiple logos are rendered
  const rawId = React.useId();
  const uid = rawId.replace(/:/g, ''); // Sanitize for SVG url(#id) usage
  
  // --- COLOR LOGIC ---

  // 1. Liquid Gradients (For Face/Avatar variant)
  const getGradient = (c: string) => {
      switch (c) {
          case 'rose': return 'from-rose-400 via-pink-500 to-fuchsia-600';
          case 'emerald': return 'from-emerald-400 via-teal-500 to-cyan-600';
          case 'amber': return 'from-amber-400 via-orange-500 to-red-500';
          default: return 'from-indigo-500 via-purple-500 to-pink-500';
      }
  };

  // 2. Core Colors (For Crystalline Symbol variant)
  // UPDATED: Shifted to 500 weight for better visibility on white
  const getCoreColor = (c: string) => {
      switch (c) {
          case 'rose': return '#f43f5e'; // rose-500
          case 'emerald': return '#10b981'; // emerald-500
          case 'amber': return '#f59e0b'; // amber-500
          default: return '#6366f1'; // indigo-500
      }
  };

  // 3. Glass Tint (Stronger colors for visibility on white backgrounds)
  const getGlassTint = (c: string) => {
      switch (c) {
          case 'rose': return '#f43f5e'; // rose-500
          case 'emerald': return '#10b981'; // emerald-500
          case 'amber': return '#f59e0b'; // amber-500
          default: return '#6366f1'; // indigo-500
      }
  };

  const gradientColors = getGradient(color);
  const activeCore = getCoreColor(color);
  const glassTint = getGlassTint(color);

  // --- RENDER: AVATAR FACE (Liquid Style) ---
  // Used in Chat Bubbles to match the main Avatar character
  if (variant === 'face') {
      const shadowColor = color === 'rose' ? 'rgba(244,63,94,0.5)' : 
                          color === 'emerald' ? 'rgba(16,185,129,0.5)' : 
                          color === 'amber' ? 'rgba(245,158,11,0.5)' : 
                          'rgba(99, 102, 241, 0.5)';
      return (
        <div 
            className={`relative flex items-center justify-center select-none ${className}`}
            style={{ width: size, height: size }}
        >
          <div 
            className={`absolute inset-0 transition-all duration-500 bg-gradient-to-br ${gradientColors} rounded-[30%_70%_70%_30%_/_30%_30%_70%_70%]`}
            style={{ boxShadow: `0 0 20px ${shadowColor}` }}
          >
             {/* Subsurface Glow */}
             <div className="absolute inset-0 rounded-[inherit] bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.5),_transparent)] mix-blend-overlay"></div>
          </div>
          {/* Eyes */}
          <div className="relative z-10 flex gap-[20%] mb-[10%] w-[60%] justify-center">
                <div className="w-[20%] aspect-[2/3] bg-white rounded-full shadow-sm"></div>
                <div className="w-[20%] aspect-[2/3] bg-white rounded-full shadow-sm"></div>
          </div>
        </div>
      );
  }

  // --- RENDER: BRAND SYMBOL (Crystalline Container) ---
  // Used in Sidebar, Headers, Splash Screen
  return (
    <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible drop-shadow-md">
            <defs>
                {/* Material: Frosted Glass Body (Tinted) */}
                {/* Increased opacity/color to ensure visibility on Light Mode */}
                <linearGradient id={`glassBody-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={glassTint} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={glassTint} stopOpacity="0.05" />
                </linearGradient>
                
                {/* Material: Refractive Rim (Edge Light) */}
                {/* Switched from White to Tint -> Core -> Tint for high-contrast edges */}
                <linearGradient id={`glassRim-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={glassTint} stopOpacity="0.8" />
                    <stop offset="50%" stopColor={activeCore} stopOpacity="0.6" />
                    <stop offset="100%" stopColor={glassTint} stopOpacity="0.4" />
                </linearGradient>

                {/* Effect: Inner Singularity Glow */}
                <filter id={`coreGlow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                    <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>

                {/* Effect: Tinted Shell Shadow */}
                <filter id={`shellShadow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor={glassTint} floodOpacity="0.15" />
                </filter>
            </defs>

            {/* 1. The Container (Life OS) - 4-Lobed Aerogel Shell */}
            <path 
                d="M 50 5 C 58 5 72 32 95 50 C 72 68 58 95 50 95 C 42 95 28 68 5 50 C 28 32 42 5 50 5 Z"
                fill={`url(#glassBody-${uid})`}
                stroke={`url(#glassRim-${uid})`}
                strokeWidth="2"
                className="transition-all duration-1000"
                style={{ 
                    filter: `url(#shellShadow-${uid})` 
                }}
            />

            {/* 2. The Singularity (Liquid Soul) - Inner Core */}
            {/* The energy source that powers the OS */}
            <circle 
                cx="50" 
                cy="50" 
                r="14" 
                fill={activeCore}
                filter={`url(#coreGlow-${uid})`}
                className={`transition-colors duration-500 ${animated ? "animate-pulse" : ""}`}
                style={{ 
                    transformOrigin: 'center',
                    animationDuration: '3s'
                }}
            />
            
            {/* 3. Specular Highlight (The Gloss) */}
            <circle cx="40" cy="40" r="8" fill="white" fillOpacity="0.4" pointerEvents="none" />
        </svg>
    </div>
  );
};
